// =============================================================================
// SIREPPAR - Formulario de Interacción con Partido (Alta/Edición)
// =============================================================================

import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { interaccionService, partidoService, expedienteService } from '../../services/api';
import { tipoTramiteLabel } from '../../utils/tiposExpediente';

const SIN_DEFINIR_PARTIDO_ID = '5312e1f8-150a-4413-b963-989d961f75ae';

// =============================================================================
// Esquema de Validación
// =============================================================================

const interaccionSchema = z.object({
  partidoId:        z.string().uuid('Seleccioná un partido'),
  expedienteId:     z.string().optional(),
  tipo:             z.enum(['WHATSAPP', 'LLAMADA', 'EMAIL', 'PRESENCIAL', 'VIDEOLLAMADA', 'NOTA_INTERNA']),
  contactoNombre:   z.string().min(2, 'Nombre requerido').max(200),
  contactoCargo:    z.string().max(200).optional(),
  contactoTelefono: z.string().max(50).optional(),
  fechaInteraccion: z.string().min(1, 'Fecha requerida'),
  resumen:          z.string().min(5, 'Mínimo 5 caracteres').max(500),
  detalle:          z.string().max(5000).optional(),
  estado:           z.enum(['PENDIENTE', 'RESUELTO']),
  prioridad:        z.enum(['ALTA', 'MEDIA', 'BAJA']),
  extrapartidaria:  z.boolean(),
  entidad:          z.string().max(300).optional(),
});

type FormData = z.infer<typeof interaccionSchema>;

// =============================================================================
// Componente
// =============================================================================

export default function InteraccionFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;
  const [searchParams] = useSearchParams();
  const prefilledPartidoId = searchParams.get('partidoId') ?? '';
  const prefilledExpedienteId = searchParams.get('expedienteId') ?? '';
  const location = useLocation();

  const [contactoSeleccionado, setContactoSeleccionado] = useState('');
  const [adjuntos, setAdjuntos] = useState<{ path: string; name: string }[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showModalSeguimiento, setShowModalSeguimiento] = useState(false);
  const [interaccionesAbiertas, setInteraccionesAbiertas] = useState<any[]>([]);
  const partidoModalDismissedRef = useRef<string>('');

  // Cargar datos si es edición
  const { data: interaccion, isLoading: loadingInteraccion } = useQuery({
    queryKey: ['interaccion', id],
    queryFn: () => interaccionService.getById(id!),
    enabled: isEdit,
  });

  // Cargar lista de partidos para el select
  const { data: partidosData } = useQuery({
    queryKey: ['partidos-select'],
    queryFn: async () => {
      const res = await partidoService.list({ pageSize: 200 });
      return res.data.data;
    },
  });
  
  const partidos = partidosData ?? [];  

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(interaccionSchema),
    defaultValues: { tipo: 'WHATSAPP', estado: 'PENDIENTE', prioridad: 'MEDIA', extrapartidaria: false, partidoId: prefilledPartidoId, expedienteId: prefilledExpedienteId },
  });

  const watchedPartidoId = watch('partidoId');
  const watchedExtrapartidaria = watch('extrapartidaria');

  // Cargar nómina cuando cambia el partido
  const { data: autoridadesData } = useQuery({
    queryKey: ['nomina-partido', watchedPartidoId],
    queryFn: () => partidoService.getAutoridades(watchedPartidoId),
    enabled: !!watchedPartidoId,
  });

  // Cargar expedientes cuando cambia el partido
  const { data: expedientesData } = useQuery({
    queryKey: ['expedientes-partido', watchedPartidoId],
    queryFn: () => expedienteService.list({ partidoId: watchedPartidoId, pageSize: 100 }),
    enabled: !!watchedPartidoId && watchedPartidoId !== SIN_DEFINIR_PARTIDO_ID,
  });

  const expedientes = expedientesData?.data?.data ?? [];

  const { data: interaccionesAbiertasData } = useQuery({
    queryKey: ['interacciones-abiertas', watchedPartidoId],
    queryFn: () => interaccionService.list({ partidoId: watchedPartidoId, estado: 'PENDIENTE', pageSize: '5' }),
    enabled: !isEdit && !!watchedPartidoId && watchedPartidoId !== SIN_DEFINIR_PARTIDO_ID,
  });  

  // Combinar autoridades y apoderados en una lista de contactos
  const autoridades = autoridadesData?.data?.data?.autoridades ?? [];
  const apoderados  = autoridadesData?.data?.data?.apoderados  ?? [];

  const contactos = [
    ...autoridades.map((a: any) => ({
      id:       a.id,
      nombre:   `${a.nombre} ${a.apellido}`,
      cargo:    a.mandatos?.[0]?.cargo ?? 'Autoridad partidaria',
      telefono: a.telefono ?? '',
    })),
    ...apoderados.map((p: any) => ({
      id:       p.id,
      nombre:   `${p.nombre} ${p.apellido}`,
      cargo:    p.tipo ?? 'Apoderado',
      telefono: p.telefono ?? '',
    })),
  ];

  // Limpiar selector de nómina cuando cambia el partido
  useEffect(() => {
    setContactoSeleccionado('');
  }, [watchedPartidoId]);
  
  // Pre-llenar partido cuando carga la lista
  useEffect(() => {
    if (!isEdit && prefilledPartidoId && partidos.length > 0) {
      setValue('partidoId', prefilledPartidoId);
    }
  }, [partidos]);

    // Pre-llenar detalle cuando se viene desde carga de afiliados
    useEffect(() => {
      const preDetalle = (location.state as any)?.contenidoPrecargado;
      if (!isEdit && preDetalle) {
        setValue('detalle', preDetalle);
      }
    }, []);  

  // Pre-llenar detalle cuando se selecciona "Sin definir"
  useEffect(() => {
    if (watchedPartidoId === SIN_DEFINIR_PARTIDO_ID && !isEdit) {
      setValue('detalle', 'Nombre Partido: Ej. Partido del Trabajo Chubutense\n\nUna vez creado el partido, modificar el campo Partido y colocar la opción correcta.');
    }
  }, [watchedPartidoId, isEdit, setValue]);  

  useEffect(() => {
    if (watchedExtrapartidaria && !watchedPartidoId) {
      setValue('partidoId', SIN_DEFINIR_PARTIDO_ID);
    }
  }, [watchedExtrapartidaria]);  

  // Cargar datos en el form al editar
  useEffect(() => {
    if (interaccion?.data && isEdit) {
      const d = interaccion.data;
      reset({
        partidoId:        d.partidoId,
        expedienteId:     d.expedienteId ?? '',
        tipo:             d.tipo,
        contactoNombre:   d.contactoNombre,
        contactoCargo:    d.contactoCargo ?? '',
        contactoTelefono: d.contactoTelefono ?? '',
        fechaInteraccion: d.fechaInteraccion?.slice(0, 16) ?? '',
        resumen:          d.resumen,
        detalle:          d.detalle ?? '',
        estado:           d.estado,
        prioridad:        d.prioridad ?? 'MEDIA',
        extrapartidaria:  d.extrapartidaria ?? false,
        entidad:          d.entidad ?? '',
      });
      if (d.adjuntos?.length) {
        setAdjuntos(d.adjuntos.map((path: string) => ({
          path,
          name: path.split('/').pop()?.replace(/^[^-]+-/, '') ?? path,
        })));
      }
    }
  }, [interaccion, isEdit, reset]);

  useEffect(() => {
    if (isEdit || !watchedPartidoId || watchedPartidoId === SIN_DEFINIR_PARTIDO_ID) return;
    if (watchedPartidoId === partidoModalDismissedRef.current) return;
    const abiertas = interaccionesAbiertasData?.data ?? [];
    if (abiertas.length > 0) {
      setInteraccionesAbiertas(abiertas);
      setShowModalSeguimiento(true);
    }
  }, [interaccionesAbiertasData, watchedPartidoId]);
  
  // Subir archivos adjuntos
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingFile(true);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('archivo', file);
        const res = await interaccionService.uploadAdjunto(formData);
        setAdjuntos(prev => [...prev, { path: res.data.path, name: res.data.originalName }]);
      }
    } catch {
      toast.error('Error al subir el archivo');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  // Autocompletar campos de contacto desde la nómina
  const handleSelectContacto = (contactoId: string) => {
    setContactoSeleccionado(contactoId);
    const contacto = contactos.find(c => c.id === contactoId);
    if (contacto) {
      setValue('contactoNombre',   contacto.nombre);
      setValue('contactoCargo',    contacto.cargo);
      setValue('contactoTelefono', contacto.telefono);
    }
  };

  const mutation = useMutation({
    mutationFn: (data: FormData) => {
      const payload = {
        ...data,
        fechaInteraccion: new Date(data.fechaInteraccion).toISOString(),
        expedienteId:     data.expedienteId     || undefined,
        contactoCargo:    data.contactoCargo    || undefined,
        contactoTelefono: data.contactoTelefono || undefined,
        detalle:          data.detalle          || undefined,
        adjuntos:         adjuntos.map(a => a.path),
        prioridad:        data.prioridad,
        extrapartidaria:  data.extrapartidaria,
        entidad:          data.entidad || undefined,
      };
      return isEdit
        ? interaccionService.update(id!, payload)
        : interaccionService.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Interacción actualizada' : 'Interacción registrada');
      queryClient.invalidateQueries({ queryKey: ['interacciones'] });
      navigate('/interacciones');
    },
  });

  if (isEdit && loadingInteraccion) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Encabezado */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/interacciones')} className="text-gray-500 hover:text-gray-700 text-sm">
          ← Volver
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Editar interacción' : 'Nueva interacción'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(data => mutation.mutate(data))} className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">

        {/* Datos principales */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 pb-2 mb-4">
            Datos principales
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de interacción <span className="text-red-500">*</span>
              </label>
              <select {...register('tipo')} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                <option value="WHATSAPP">WhatsApp</option>
                <option value="LLAMADA">Llamada telefónica</option>
                <option value="EMAIL">Email</option>
                <option value="PRESENCIAL">Reunión presencial</option>
                <option value="VIDEOLLAMADA">Videollamada</option>
                <option value="NOTA_INTERNA">Nota interna</option>
              </select>
              {errors.tipo && <p className="text-red-500 text-xs mt-1">{errors.tipo.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha y hora <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                {...register('fechaInteraccion')}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">Cuándo ocurrió (no cuándo se registra)</p>
              {errors.fechaInteraccion && <p className="text-red-500 text-xs mt-1">{errors.fechaInteraccion.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Partido <span className="text-red-500">*</span>
              </label>
              <select
                  {...register('partidoId')}
                  disabled={watchedExtrapartidaria}
                  className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${watchedExtrapartidaria ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                >
                <option value="">Seleccioná un partido...</option>
                <option value={SIN_DEFINIR_PARTIDO_ID}>⚠ Sin definir / no registrado aún</option>
                {partidos.filter((p: any) => p.id !== SIN_DEFINIR_PARTIDO_ID).map((p: any) => (
                <option key={p.id} value={p.id}>{p.denominacion}</option>
              ))}
              </select>
              {errors.partidoId && <p className="text-red-500 text-xs mt-1">{errors.partidoId.message}</p>}
            </div>
            {watchedExtrapartidaria && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Entidad <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Ej: Juzgado Electoral, Ministerio de Gobierno..."
                  {...register('entidad')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                {errors.entidad && <p className="text-red-500 text-xs mt-1">{errors.entidad.message}</p>}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expediente asociado</label>
              {expedientes.length > 0 ? (
                <select
                  {...register('expedienteId')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Sin expediente asociado</option>
                  {expedientes.map((e: any) => (
                    <option key={e.id} value={e.id}>
                      {e.numero} — {tipoTramiteLabel(e.tipo)}{e.asunto ? ` · ${e.asunto}` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  placeholder="Ej: 2024-031 (opcional)"
                  {...register('expedienteId')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
              <select {...register('prioridad')} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                <option value="ALTA">Alta</option>
                <option value="MEDIA">Media</option>
                <option value="BAJA">Baja</option>
              </select>
            </div>

            <div className="flex items-center gap-3 pt-6">
              <input
                type="checkbox"
                id="extrapartidaria"
                {...register('extrapartidaria')}
                className="h-4 w-4 rounded border-gray-300 text-blue-600"
              />
              <label htmlFor="extrapartidaria" className="text-sm font-medium text-gray-700">
                Interacción extrapartidaria
              </label>
            </div>
          </div>
        </div>

        {/* Contacto */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 pb-2 mb-4">
            Contacto
          </h2>

          {/* Selector de nómina — solo aparece si hay partido seleccionado con contactos cargados */}
          {watchedPartidoId && contactos.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <label className="block text-xs font-semibold text-blue-700 mb-1">
                Seleccionar de la nómina del partido
              </label>
              <select
                value={contactoSeleccionado}
                onChange={e => handleSelectContacto(e.target.value)}
                className="w-full border border-blue-300 rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="">Elegí un contacto para autocompletar...</option>
                {contactos.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.nombre} — {c.cargo}
                  </option>
                ))}
              </select>
              <p className="text-xs text-blue-500 mt-1">Los campos se completan automáticamente pero podés editarlos.</p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('contactoNombre')}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              {errors.contactoNombre && <p className="text-red-500 text-xs mt-1">{errors.contactoNombre.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cargo</label>
              <input
                type="text"
                placeholder="Ej: Apoderado titular"
                {...register('contactoCargo')}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
              <input
                type="text"
                placeholder="+54 9 11 ..."
                {...register('contactoTelefono')}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Contenido */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 pb-2 mb-4">
            Contenido
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resumen <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Qué se trató en pocas palabras..."
                {...register('resumen')}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              {errors.resumen && <p className="text-red-500 text-xs mt-1">{errors.resumen.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas y detalles</label>
              <textarea
                rows={4}
                placeholder="Detalles, compromisos asumidos, próximos pasos..."
                {...register('detalle')}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none"
              />
            </div>

            <div className="w-48">
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select {...register('estado')} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                <option value="PENDIENTE">Pendiente de respuesta</option>
                <option value="RESUELTO">Resuelto</option>
              </select>
            </div>
          </div>
        </div>
        {/* Archivos adjuntos */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 pb-2 mb-4">
            Archivos adjuntos
          </h2>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adjuntar archivos</label>
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                disabled={uploadingFile}
                className="block text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-gray-300 file:text-sm file:text-gray-700 file:bg-white hover:file:bg-gray-50 disabled:opacity-50"
              />
              {uploadingFile && <p className="text-xs text-gray-400 mt-1">Subiendo...</p>}
              <p className="text-xs text-gray-400 mt-1">PDF, imágenes, Word, Excel. Máx. 50 MB por archivo.</p>
            </div>
            {adjuntos.length > 0 && (
              <ul className="space-y-1">
                {adjuntos.map((a, i) => (
                  <li key={i} className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-1.5">
                    <span className="text-gray-700 truncate">{a.name}</span>
                    <button
                      type="button"
                      onClick={() => setAdjuntos(prev => prev.filter((_, idx) => idx !== i))}
                      className="text-red-500 hover:text-red-700 ml-3 shrink-0 text-xs"
                    >
                      Quitar
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Acciones */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={() => navigate('/interacciones')}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
          >
            {mutation.isPending ? 'Guardando...' : isEdit ? 'Actualizar' : 'Guardar interacción'}
          </button>
        </div>
      </form>
      {showModalSeguimiento && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Este partido tiene interacciones abiertas
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {interaccionesAbiertas.length === 1
                ? 'Hay una interacción pendiente de respuesta. ¿Querés agregar una nota al seguimiento o es un motivo diferente?'
                : `Hay ${interaccionesAbiertas.length} interacciones pendientes. ¿Querés agregar una nota al seguimiento de alguna o es un motivo diferente?`}
            </p>
                        <div className="space-y-2 mb-4">
              {interaccionesAbiertas.map(interaccion => (
                <button
                  key={interaccion.id}
                  type="button"
                  onClick={() => {
                    partidoModalDismissedRef.current = watchedPartidoId;
                    setShowModalSeguimiento(false);
                    navigate(`/interacciones/${interaccion.id}`, { state: { focusNota: true } });
                  }}
                  className="w-full text-left px-3 py-3 border border-blue-200 rounded-md hover:bg-blue-50"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Agregar nota a esta interacción</span>
                    <span className="text-xs text-gray-400">{new Date(interaccion.fechaInteraccion).toLocaleDateString('es-AR')}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">{interaccion.resumen}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Contacto: <span className="font-medium">{interaccion.contactoNombre}</span>
                    {interaccion.contactoCargo && <span className="text-gray-400"> · {interaccion.contactoCargo}</span>}
                  </p>
                  {interaccion.detalle && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{interaccion.detalle}</p>
                  )}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                partidoModalDismissedRef.current = watchedPartidoId;
                setShowModalSeguimiento(false);
              }}
              className="w-full px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium"
            >
              Es otro motivo — crear nueva interacción
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
